<script setup lang="ts">
import { DeleteButton } from '@/components/Buttons'
import { BaseCard } from '@/components/Cards'
import checkpointApi from '@/services/checkpoint.service'
import type { AssociatedDeviceModel } from '@/types/models/DeviceModel'
import { ECheckpointsPerm } from '@/types/permissions/CheckpointsPermissionsEnum'
import { userHasPermission } from '@/utils/permissionsUtils'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import type { CheckpointModel } from '../../../../types/models/CheckpointModel'

const route = useRoute()
const eventId = computed(() => parseInt(route.params.event_id.toString()))

type Props = {
    device: AssociatedDeviceModel
    checkpoint?: Partial<CheckpointModel>
}
const props = defineProps<Props>()
const emit = defineEmits<{ (e: 'refresh'): void }>()

const dissociateDevice = () => {
    if (!props.checkpoint?.id) return
    checkpointApi
        .unassociateDeviceFromCheckpoint(eventId.value, props.checkpoint.id, props.device.device_id)
        .then(() => emit('refresh'))
}

const hasEditCheckpointPerm = computed(() => userHasPermission(ECheckpointsPerm.CHECKPOINTS_EDIT))
const borderBottom = computed(() => (hasEditCheckpointPerm.value ? 'inherit' : 0))
</script>

<template>
    <BaseCard class="dissociate-device-card fit-content">
        <template #content>
            {{ device.name }}
        </template>
        <template v-if="hasEditCheckpointPerm" #footer>
            <DeleteButton
                :label="$t('checkpoint.detail.dissociateDevice')"
                class="full-width dissociate-button"
                @click="dissociateDevice" />
        </template>
    </BaseCard>
</template>

<style scoped lang="scss">
.dissociate-device-card {
    & :deep(.card-content) {
        text-align: center;
        border: 1px solid $primary;
        border-bottom: v-bind(borderBottom);
    }
    & :deep(.card-footer) {
        border-top: unset;
    }
    & :deep(.button) {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }
}
.dissociate-button {
    &:deep(span) {
        display: flex;
        flex-direction: row;
        gap: 8px;
    }
}
</style>
